<template>
  <div class="container">
    <h4 class="is-size-6-mobile is-size-5-tablet">
      {{ $t("creationSummaryReport.page_title") }}
    </h4>

    <!-- Filters -->
    <div class="box">
      <div class="columns is-multiline is-mobile">
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <div class="control">
            <label class="label">{{ $t("creationSummaryReport.from_date") }}:</label>
            <date-picker
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              v-model="from"
              :disabled-date="disabledAfterToday"
              :clearable="false"
              name="from"
              class="is-fullwidth"
            ></date-picker>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <div class="control">
            <label class="label">{{ $t("creationSummaryReport.to_date") }}:</label>
            <date-picker
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              v-model="to"
              :disabled-date="disabledAfterToday"
              :clearable="false"
              name="to"
              class="is-fullwidth"
            ></date-picker>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <div class="control">
            <label class="label">{{ $t("creationSummaryReport.metric") }}:</label>
            <div class="select is-fullwidth">
              <select v-model="metric">
                <option v-for="m in metricKeys" :key="m" :value="m">{{ metricLabel(m) }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-4-desktop">
          <label class="label is-invisible">{{ $t("creationSummaryReport.search") }}</label>
          <div class="buttons is-centered-mobile">
            <button class="button is-rounded is-fullwidth-mobile" @click="fetchData()">
              <span class="icon"><i class="fal fa-search"></i></span>
              <span>{{ $t("creationSummaryReport.search") }}</span>
            </button>
            <button class="button is-light is-rounded is-fullwidth-mobile" @click="quickWindow(1)">
              {{ $t("creationSummaryReport.yesterday") }}
            </button>
            <button class="button is-light is-rounded is-fullwidth-mobile" @click="quickWindow(0)">
              {{ $t("creationSummaryReport.today") }}
            </button>
            <button
              class="button is-primary is-rounded is-fullwidth-mobile"
              @click="exportExcel()"
              :disabled="!report || !report.available"
            >
              <span class="icon"><i class="fal fa-file-excel"></i></span>
              <span>{{ $t("creationSummaryReport.export_excel") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pay-type tabs, same four as the shipment list -->
    <div class="tabs is-boxed report-tabs">
      <ul>
        <li v-for="tabKey in tabKeys" :key="tabKey" :class="{'is-active': tab === tabKey}" @click="tab = tabKey">
          <a>{{ $t("creationSummaryReport.tab_" + tabKey) }}</a>
        </li>
      </ul>
    </div>

    <Loading v-if="loading" />

    <div class="notification is-warning is-light" v-if="report && !loading && !report.available">
      {{ $t("creationSummaryReport.unavailable") }}
    </div>

    <template v-if="report && !loading && report.available">
      <!-- Window totals: the shipment list's summary line for this window + tab -->
      <div class="box totals-box">
        <p class="has-text-weight-bold">
          {{ $t("creationSummaryReport.total_bills") }}: {{ formatNumber(totals.b) }}
          &nbsp;|&nbsp;
          {{ $t("creationSummaryReport.total_freight") }}: {{ formatMoney(totals.f) }}
          &nbsp;|&nbsp;
          {{ $t("creationSummaryReport.total_price") }}: {{ formatMoney(totals.v) }}
          &nbsp;|&nbsp;
          {{ $t("creationSummaryReport.total_pieces") }}: {{ formatNumber(totals.q) }} {{ $t("creationSummaryReport.box_unit") }}
        </p>
        <p class="is-size-7">
          {{ $t("creationSummaryReport.express") }}: {{ formatNumber(totals.express.b) }} {{ $t("creationSummaryReport.bill_unit") }} / {{ formatMoney(totals.express.f) }}
          &nbsp;|&nbsp;
          {{ $t("creationSummaryReport.logistics") }}: {{ formatNumber(totals.logistics.b) }} {{ $t("creationSummaryReport.bill_unit") }} / {{ formatMoney(totals.logistics.f) }}
          <span v-if="totals.other.b">
            &nbsp;|&nbsp;
            {{ $t("creationSummaryReport.other_type") }}: {{ formatNumber(totals.other.b) }} {{ $t("creationSummaryReport.bill_unit") }} / {{ formatMoney(totals.other.f) }}
          </span>
        </p>
        <p class="is-size-7 has-text-grey" v-if="report.as_of">
          {{ $t("creationSummaryReport.as_of") }}: {{ formatDateTime(report.as_of) }}
        </p>
        <p class="is-size-7 has-text-danger" v-if="report.missing_days && report.missing_days.length">
          {{ $t("creationSummaryReport.missing_days") }}: {{ missingDaysLabel }}
        </p>
        <p class="is-size-7 has-text-grey">{{ $t("creationSummaryReport.definition_note") }}</p>
      </div>

      <!-- Level 1: per day -->
      <div class="box">
        <h5 class="is-size-6">{{ $t("creationSummaryReport.daily_title") }} — {{ metricLabel(metric) }}</h5>
        <p class="is-size-7 has-text-grey">{{ $t("creationSummaryReport.daily_hint") }}</p>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(dailyLabels, dailyDatasets, 320, 700)">
            <grouped-bar-chart
              :labels="dailyLabels"
              :datasets="dailyDatasets"
              :unit="metricUnit(metric)"
              :clickable="true"
              @bar-click="onDayBarClick"
            />
          </div>
        </div>
        <div class="table-container">
          <table class="table is-bordered is-striped is-fullwidth is-narrow">
            <thead>
              <tr>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.date") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.express") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.logistics") }}</th>
                <th colspan="2" class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.other_type") }}</th>
                <th colspan="4" class="has-text-centered">{{ $t("creationSummaryReport.total") }}</th>
                <th rowspan="2"></th>
              </tr>
              <tr>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.pieces") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.price") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in dailyRows" :key="row.key" :class="{'is-missing-row': isMissingDay(row.key)}">
                <td>{{ formatDate(row.key) }} <span v-if="isMissingDay(row.key)" class="tag is-danger is-light is-small">{{ $t("creationSummaryReport.not_built") }}</span></td>
                <td class="has-text-right">{{ dayCell(row, row.express.b) }}</td>
                <td class="has-text-right">{{ dayCell(row, row.express.f) }}</td>
                <td class="has-text-right">{{ dayCell(row, row.logistics.b) }}</td>
                <td class="has-text-right">{{ dayCell(row, row.logistics.f) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ dayCell(row, row.other.b) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ dayCell(row, row.other.f) }}</td>
                <td class="has-text-right"><strong>{{ dayCell(row, row.total.b) }}</strong></td>
                <td class="has-text-right">{{ dayCell(row, row.total.q) }}</td>
                <td class="has-text-right"><strong>{{ dayCell(row, row.total.f) }}</strong></td>
                <td class="has-text-right">{{ dayCell(row, row.total.v) }}</td>
                <td class="has-text-centered">
                  <button class="button is-small is-rounded" @click="selectDay(row.key)" v-if="dailyRows.length > 1 && !row.missing">
                    {{ $t("creationSummaryReport.view_day") }}
                  </button>
                </td>
              </tr>
              <tr class="grand-total-row" v-if="dailyRows.length > 1">
                <td><strong>{{ $t("creationSummaryReport.total") }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.express.b) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.express.f) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.logistics.b) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.logistics.f) }}</strong></td>
                <td class="has-text-right" v-if="hasOther"><strong>{{ formatNumber(totals.other.b) }}</strong></td>
                <td class="has-text-right" v-if="hasOther"><strong>{{ formatNumber(totals.other.f) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.b) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.q) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.f) }}</strong></td>
                <td class="has-text-right"><strong>{{ formatNumber(totals.v) }}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Level 2: time of day -->
      <div class="box">
        <div class="columns is-vcentered is-mobile">
          <div class="column">
            <h5 class="is-size-6">{{ $t("creationSummaryReport.slots_title") }} — {{ metricLabel(metric) }}</h5>
            <p class="is-size-7 has-text-grey">{{ $t("creationSummaryReport.slots_hint") }}</p>
          </div>
          <div class="column is-narrow">
            <div class="select is-small">
              <select v-model.number="slotHours">
                <option v-for="h in [1, 2, 3, 6]" :key="h" :value="h">{{ h }} {{ $t("creationSummaryReport.hour_unit") }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(slotLabelList, slotDatasets, 320, 700)">
            <grouped-bar-chart
              :labels="slotLabelList"
              :datasets="slotDatasets"
              :unit="metricUnit(metric)"
            />
          </div>
        </div>
        <div class="table-container">
          <table class="table is-bordered is-striped is-fullwidth is-narrow">
            <thead>
              <tr>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.slot") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.express") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.logistics") }}</th>
                <th colspan="2" class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.other_type") }}</th>
                <th colspan="4" class="has-text-centered">{{ $t("creationSummaryReport.total") }}</th>
              </tr>
              <tr>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.pieces") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.price") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in slotRows" :key="row.key" :class="{'is-empty-row': row.total.b === 0}">
                <td>{{ row.label }}</td>
                <td class="has-text-right">{{ formatNumber(row.express.b) }}</td>
                <td class="has-text-right">{{ formatNumber(row.express.f) }}</td>
                <td class="has-text-right">{{ formatNumber(row.logistics.b) }}</td>
                <td class="has-text-right">{{ formatNumber(row.logistics.f) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.b) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.f) }}</td>
                <td class="has-text-right"><strong>{{ formatNumber(row.total.b) }}</strong></td>
                <td class="has-text-right">{{ formatNumber(row.total.q) }}</td>
                <td class="has-text-right"><strong>{{ formatNumber(row.total.f) }}</strong></td>
                <td class="has-text-right">{{ formatNumber(row.total.v) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Level 3: origin province → branch -->
      <div class="box">
        <h5 class="is-size-6">{{ $t("creationSummaryReport.provinces_title") }} — {{ metricLabel(metric) }}</h5>
        <p class="is-size-7 has-text-grey">{{ $t("creationSummaryReport.provinces_hint") }}</p>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(provinceLabels, provinceDatasets, 360, 900)">
            <grouped-bar-chart
              :labels="provinceLabels"
              :datasets="provinceDatasets"
              :unit="metricUnit(metric)"
              :clickable="true"
              @bar-click="onProvinceBarClick"
            />
          </div>
        </div>
        <div class="table-container">
          <table class="table is-bordered is-striped is-fullwidth is-narrow is-hoverable">
            <thead>
              <tr>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.province") }}</th>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.branch_count") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.express") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.logistics") }}</th>
                <th colspan="2" class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.other_type") }}</th>
                <th colspan="4" class="has-text-centered">{{ $t("creationSummaryReport.total") }}</th>
                <th rowspan="2"></th>
              </tr>
              <tr>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.pieces") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.price") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in provinceRows"
                :key="row.key"
                :class="{'is-selected-row': selectedProvinceId === row.key}"
                class="is-clickable"
                @click="selectProvince(row.key)"
              >
                <td>{{ row.label }}</td>
                <td class="has-text-right">{{ formatNumber(row.branchCount) }}</td>
                <td class="has-text-right">{{ formatNumber(row.express.b) }}</td>
                <td class="has-text-right">{{ formatNumber(row.express.f) }}</td>
                <td class="has-text-right">{{ formatNumber(row.logistics.b) }}</td>
                <td class="has-text-right">{{ formatNumber(row.logistics.f) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.b) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.f) }}</td>
                <td class="has-text-right"><strong>{{ formatNumber(row.total.b) }}</strong></td>
                <td class="has-text-right">{{ formatNumber(row.total.q) }}</td>
                <td class="has-text-right"><strong>{{ formatNumber(row.total.f) }}</strong></td>
                <td class="has-text-right">{{ formatNumber(row.total.v) }}</td>
                <td class="has-text-centered">
                  <button class="button is-small is-rounded" @click.stop="selectProvince(row.key)">
                    {{ $t("creationSummaryReport.view_branches") }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Branch table (province-scoped when one is selected) -->
      <div class="box" ref="branchBox">
        <div class="columns is-multiline is-vcentered">
          <div class="column is-12-mobile is-4-desktop">
            <h5 class="is-size-6">
              {{ $t("creationSummaryReport.branches_title") }}
              <span v-if="selectedProvinceId !== null">— {{ provinceName(selectedProvinceId) }}</span>
            </h5>
            <p class="is-size-7 has-text-grey">{{ $t("creationSummaryReport.branches_hint") }}</p>
          </div>
          <div class="column is-12-mobile is-3-desktop">
            <div class="select is-small is-fullwidth">
              <select v-model="branchType">
                <option value="">{{ $t("creationSummaryReport.branch_type_all") }}</option>
                <option v-for="type in branchTypes" :key="type" :value="type">{{ branchTypeLabel(type) }}</option>
              </select>
            </div>
          </div>
          <div class="column is-12-mobile is-3-desktop">
            <input class="input is-small" type="text" v-model="branchSearch" :placeholder="$t('creationSummaryReport.branch_search')" />
          </div>
          <div class="column is-12-mobile is-2-desktop has-text-right">
            <button class="button is-small is-rounded" v-if="selectedProvinceId !== null" @click="selectedProvinceId = null">
              {{ $t("creationSummaryReport.all_provinces") }}
            </button>
          </div>
        </div>
        <div class="table-container branch-table">
          <table class="table is-bordered is-striped is-fullwidth is-narrow is-hoverable">
            <thead>
              <tr>
                <th rowspan="2" class="has-text-centered is-vcentered">#</th>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.branch") }}</th>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.branch_type") }}</th>
                <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.province") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.express") }}</th>
                <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.logistics") }}</th>
                <th colspan="2" class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.other_type") }}</th>
                <th colspan="4" class="has-text-centered">{{ $t("creationSummaryReport.total") }}</th>
                <th rowspan="2"></th>
              </tr>
              <tr>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.pieces") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                <th class="has-text-centered">{{ $t("creationSummaryReport.price") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in branchRows"
                :key="row.key"
                :class="{'is-selected-row': selectedBranchId === row.key}"
                class="is-clickable"
                @click="selectBranch(row.key)"
              >
                <td>{{ index + 1 }}</td>
                <td>{{ row.label }}</td>
                <td>{{ branchTypeLabel(row.type) }}</td>
                <td>{{ provinceName(row.provinceId) }}</td>
                <td class="has-text-right">{{ formatNumber(row.express.b) }}</td>
                <td class="has-text-right">{{ formatNumber(row.express.f) }}</td>
                <td class="has-text-right">{{ formatNumber(row.logistics.b) }}</td>
                <td class="has-text-right">{{ formatNumber(row.logistics.f) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.b) }}</td>
                <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.f) }}</td>
                <td class="has-text-right"><strong>{{ formatNumber(row.total.b) }}</strong></td>
                <td class="has-text-right">{{ formatNumber(row.total.q) }}</td>
                <td class="has-text-right"><strong>{{ formatNumber(row.total.f) }}</strong></td>
                <td class="has-text-right">{{ formatNumber(row.total.v) }}</td>
                <td class="has-text-centered">
                  <button class="button is-small is-rounded" @click.stop="selectBranch(row.key)">
                    {{ $t("creationSummaryReport.view_branch") }}
                  </button>
                </td>
              </tr>
              <tr v-if="!branchRows.length">
                <td :colspan="hasOther ? 15 : 13" class="has-text-centered has-text-grey">{{ $t("creationSummaryReport.empty_state") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="is-size-7 has-text-grey" v-if="report.unmapped_branch_count">
          {{ $t("creationSummaryReport.unmapped") }}: {{ formatNumber(report.unmapped_branch_count) }}
        </p>
      </div>

      <!-- Level 4: one branch — its days and time slots -->
      <div class="box" v-if="selectedBranchId !== null" ref="branchDrill">
        <div class="columns is-vcentered is-mobile drill-header">
          <div class="column">
            <h5 class="is-size-6">
              {{ $t("creationSummaryReport.branch_drill_title") }}: {{ selectedBranchName }}
              <span class="has-text-grey is-size-7" v-if="branchReport">({{ formatDate(branchReport.from) }} – {{ formatDate(branchReport.to) }})</span>
            </h5>
          </div>
          <div class="column is-narrow">
            <button class="button is-small is-rounded" @click="closeBranch()">
              <span class="icon"><i class="fal fa-times"></i></span>
              <span>{{ $t("creationSummaryReport.close") }}</span>
            </button>
          </div>
        </div>

        <Loading v-if="branchLoading" />

        <template v-if="branchReport && !branchLoading">
          <p class="has-text-weight-bold">
            {{ $t("creationSummaryReport.total_bills") }}: {{ formatNumber(branchTotals.b) }}
            &nbsp;|&nbsp;
            {{ $t("creationSummaryReport.total_freight") }}: {{ formatMoney(branchTotals.f) }}
            &nbsp;|&nbsp;
            {{ $t("creationSummaryReport.total_price") }}: {{ formatMoney(branchTotals.v) }}
            &nbsp;|&nbsp;
            {{ $t("creationSummaryReport.total_pieces") }}: {{ formatNumber(branchTotals.q) }} {{ $t("creationSummaryReport.box_unit") }}
          </p>

          <h6 class="is-size-7 has-text-weight-bold section-title">{{ $t("creationSummaryReport.daily_title") }} — {{ metricLabel(metric) }}</h6>
          <div class="chart-scroll">
            <div class="chart-wrapper" :style="chartStyle(branchDailyLabels, branchDailyDatasets, 260, 600)">
              <grouped-bar-chart
                :labels="branchDailyLabels"
                :datasets="branchDailyDatasets"
                :unit="metricUnit(metric)"
              />
            </div>
          </div>

          <h6 class="is-size-7 has-text-weight-bold section-title">{{ $t("creationSummaryReport.slots_title") }} — {{ metricLabel(metric) }}</h6>
          <div class="chart-scroll">
            <div class="chart-wrapper" :style="chartStyle(branchSlotLabels, branchSlotDatasets, 260, 600)">
              <grouped-bar-chart
                :labels="branchSlotLabels"
                :datasets="branchSlotDatasets"
                :unit="metricUnit(metric)"
              />
            </div>
          </div>

          <div class="table-container">
            <table class="table is-bordered is-striped is-fullwidth is-narrow">
              <thead>
                <tr>
                  <th rowspan="2" class="has-text-centered is-vcentered">{{ $t("creationSummaryReport.date") }}</th>
                  <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.express") }}</th>
                  <th colspan="2" class="has-text-centered">{{ $t("creationSummaryReport.logistics") }}</th>
                  <th colspan="2" class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.other_type") }}</th>
                  <th colspan="4" class="has-text-centered">{{ $t("creationSummaryReport.total") }}</th>
                </tr>
                <tr>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                  <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.bills") }}</th>
                  <th class="has-text-centered" v-if="hasOther">{{ $t("creationSummaryReport.freight") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.bills") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.pieces") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.freight") }}</th>
                  <th class="has-text-centered">{{ $t("creationSummaryReport.price") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in branchDailyRows" :key="row.key">
                  <td>{{ formatDate(row.key) }}</td>
                  <td class="has-text-right">{{ formatNumber(row.express.b) }}</td>
                  <td class="has-text-right">{{ formatNumber(row.express.f) }}</td>
                  <td class="has-text-right">{{ formatNumber(row.logistics.b) }}</td>
                  <td class="has-text-right">{{ formatNumber(row.logistics.f) }}</td>
                  <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.b) }}</td>
                  <td class="has-text-right" v-if="hasOther">{{ formatNumber(row.other.f) }}</td>
                  <td class="has-text-right"><strong>{{ formatNumber(row.total.b) }}</strong></td>
                  <td class="has-text-right">{{ formatNumber(row.total.q) }}</td>
                  <td class="has-text-right"><strong>{{ formatNumber(row.total.f) }}</strong></td>
                  <td class="has-text-right">{{ formatNumber(row.total.v) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "vue2-datepicker";
import Loading from "@/views/Loading";
import XLSX from "xlsx";
import GroupedBarChart from "@/components/Chart/GroupedBarChart";
import {fetchCreationSummaryReport} from "@/services/creationSummaryReportService";

const TYPE_COLORS = {express: "#2EA8D9", logistics: "#E11F26", other: "#9ca3af"};
const TYPE_KEYS = ["express", "logistics", "other"];
// Which raw shipment_pay_type values each tab folds — copied from the shipment
// list's filter (ParcelListRepository), so a tab here equals the same tab there.
const TAB_PAY_TYPES = {
  all: null,
  origin: ["origin_freight_fees", "origin_freight_fees_cod"],
  cdc: ["destination_freight_fees", "destination_freight_fees_cod"],
  cod: ["origin_freight_fees_cod", "destination_freight_fees_cod"],
};

function emptyCell() {
  return {b: 0, q: 0, f: 0, v: 0};
}

function addCell(target, cell) {
  target.b += cell.b;
  target.q += cell.q;
  target.f += cell.f;
  target.v += cell.v;
}

function emptyRow(key, label) {
  return {key, label, express: emptyCell(), logistics: emptyCell(), other: emptyCell(), total: emptyCell()};
}

function typeKey(t) {
  return t === "express" || t === "logistics" ? t : "other";
}

export default {
  name: "creationSummaryReport",
  components: {DatePicker, Loading, GroupedBarChart},
  data() {
    return {
      // Backend default too: yesterday (the complete day the owner asks for) → today.
      from: moment(new Date()).subtract(1, "days").format("YYYY-MM-DD"),
      to: moment(new Date()).format("YYYY-MM-DD"),
      report: null,
      loading: false,
      requestSeq: 0,
      tab: "all",
      tabKeys: Object.keys(TAB_PAY_TYPES),
      metric: "b",
      metricKeys: ["b", "f", "v", "q"],
      slotHours: 1,
      selectedProvinceId: null,
      branchType: "",
      branchSearch: "",
      selectedBranchId: null,
      branchReport: null,
      branchLoading: false,
      branchSeq: 0,
    };
  },
  watch: {
    tab() {
      this.validateSelections();
    },
  },
  computed: {
    // Days of the window the builder never wrote — charted as nothing, never as zero revenue.
    missingDaysLabel() {
      const days = (this.report && this.report.missing_days) || [];
      const shown = days.slice(0, 8).map((d) => this.formatDate(d));
      return shown.join(", ") + (days.length > 8 ? " … (+" + (days.length - 8) + ")" : "");
    },
    filteredHourly() {
      return this.report ? this.report.hourly.filter((c) => this.payMatches(c.p)) : [];
    },
    totals() {
      return this.sumRows(this.dailyRows);
    },
    dailyRows() {
      return this.foldDaily(this.filteredHourly, this.report ? this.report.days : []);
    },
    dailyLabels() {
      return this.dailyLabelsOf(this.dailyRows);
    },
    slotRows() {
      return this.foldSlots(this.filteredHourly);
    },
    filteredBranches() {
      if (!this.report) return [];
      return this.report.branches
        .map((branch) => {
          const row = emptyRow(branch.branch_id, branch.name);
          row.type = branch.type;
          row.provinceId = branch.province_id;
          branch.cells.forEach((c) => {
            if (!this.payMatches(c.p)) return;
            addCell(row[typeKey(c.t)], c);
            addCell(row.total, c);
          });
          return row;
        })
        .filter((row) => row.total.b > 0 || row.total.f !== 0);
    },
    branchTypes() {
      const set = {};
      (this.report ? this.report.branches : []).forEach((b) => {
        if (b.type) set[b.type] = true;
      });
      return Object.keys(set).sort();
    },
    provinceRows() {
      const rows = {};
      this.filteredBranches.forEach((branch) => {
        const id = branch.provinceId || 0;
        if (!rows[id]) {
          rows[id] = emptyRow(id, this.provinceName(id));
          rows[id].branchCount = 0;
        }
        rows[id].branchCount += 1;
        TYPE_KEYS.forEach((t) => addCell(rows[id][t], branch[t]));
        addCell(rows[id].total, branch.total);
      });
      return Object.values(rows).sort((a, b) => b.total[this.metric] - a.total[this.metric]);
    },
    provinceLabels() {
      return this.provinceRows.map((r) => r.label);
    },
    branchRows() {
      const needle = this.branchSearch.trim().toLowerCase();
      return this.filteredBranches
        .filter((row) => !this.branchType || row.type === this.branchType)
        .filter((row) => this.selectedProvinceId === null || (row.provinceId || 0) === this.selectedProvinceId)
        .filter((row) => !needle || row.label.toLowerCase().includes(needle))
        .sort((a, b) => b.total[this.metric] - a.total[this.metric]);
    },
    selectedBranchName() {
      if (this.branchReport && this.branchReport.branch) return this.branchReport.branch.name;
      const row = this.filteredBranches.find((r) => r.key === this.selectedBranchId);
      return row ? row.label : "#" + this.selectedBranchId;
    },
    branchHourly() {
      return this.branchReport ? this.branchReport.hourly.filter((c) => this.payMatches(c.p)) : [];
    },
    branchDailyRows() {
      return this.foldDaily(this.branchHourly, this.branchReport ? this.branchReport.days : []);
    },
    branchSlotRows() {
      return this.foldSlots(this.branchHourly);
    },
    branchTotals() {
      return this.sumRows(this.branchDailyRows);
    },
    // Chart inputs MUST be computed (stable references): GroupedBarChart
    // watches labels/datasets and destroys + recreates the chart on change,
    // so a method call in the template would redraw every chart on every
    // keystroke in the branch search box.
    dailyDatasets() {
      return this.typeDatasets(this.dailyRows);
    },
    slotLabelList() {
      return this.slotLabels(this.slotRows);
    },
    slotDatasets() {
      return this.typeDatasets(this.slotRows);
    },
    provinceDatasets() {
      return this.typeDatasets(this.provinceRows);
    },
    branchDailyLabels() {
      return this.dailyLabelsOf(this.branchDailyRows);
    },
    branchDailyDatasets() {
      return this.typeDatasets(this.branchDailyRows);
    },
    branchSlotLabels() {
      return this.slotLabels(this.branchSlotRows);
    },
    branchSlotDatasets() {
      return this.typeDatasets(this.branchSlotRows);
    },
    hasOther() {
      return this.totals.other.b > 0;
    },
  },
  methods: {
    dayCell(row, value) {
      return row.missing ? "-" : this.formatNumber(value);
    },
    isMissingDay(day) {
      return !!(this.report && this.report.missing_days && this.report.missing_days.includes(day));
    },
    payMatches(p) {
      const allowed = TAB_PAY_TYPES[this.tab];
      return allowed === null || allowed.includes(p);
    },
    /** Same data-driven sizing as the transit report: never a clipped chart. */
    chartStyle(labels, datasets, baseHeight, baseMinWidth) {
      const groups = labels ? labels.length : 0;
      const series = datasets && datasets.length ? datasets.length : 1;
      const groupWidth = Math.max(26 * series + 22, 56);
      const longestLabel = (labels || []).reduce((max, l) => Math.max(max, String(l).length), 0);
      const labelRoom = Math.min(Math.round(longestLabel * 5), 160);
      return {
        height: baseHeight + labelRoom + "px",
        minWidth: Math.max(baseMinWidth, groups * groupWidth + 140) + "px",
      };
    },
    foldDaily(cells, days) {
      const rows = {};
      days.forEach((d) => {
        rows[d] = emptyRow(d, d);
        // Never built by the ledger: charted as a gap (null), exported blank — not as zero revenue.
        rows[d].missing = this.isMissingDay(d);
      });
      cells.forEach((c) => {
        if (!rows[c.d]) rows[c.d] = emptyRow(c.d, c.d);
        addCell(rows[c.d][typeKey(c.t)], c);
        addCell(rows[c.d].total, c);
      });
      return Object.keys(rows).sort().map((k) => rows[k]);
    },
    foldSlots(cells) {
      const size = this.slotHours;
      const rows = [];
      for (let start = 0; start < 24; start += size) {
        const end = Math.min(start + size, 24);
        rows.push(emptyRow(start, this.pad(start) + ":00–" + (end === 24 ? "24:00" : this.pad(end) + ":00")));
      }
      cells.forEach((c) => {
        const row = rows[Math.floor(c.h / size)];
        if (!row) return;
        addCell(row[typeKey(c.t)], c);
        addCell(row.total, c);
      });
      return rows;
    },
    sumRows(rows) {
      const total = emptyCell();
      total.express = emptyCell();
      total.logistics = emptyCell();
      total.other = emptyCell();
      rows.forEach((row) => {
        addCell(total, row.total);
        TYPE_KEYS.forEach((t) => addCell(total[t], row[t]));
      });
      return total;
    },
    typeDatasets(rows) {
      const hasOther = rows.some((r) => r.other.b > 0);
      return TYPE_KEYS.filter((t) => t !== "other" || hasOther).map((t) => ({
        label: this.$t("creationSummaryReport." + (t === "other" ? "other_type" : t)),
        color: TYPE_COLORS[t],
        values: rows.map((r) => (r.missing ? null : r[t][this.metric])),
      }));
    },
    dailyLabelsOf(rows) {
      return rows.map((r) => moment(r.key).format("DD/MM"));
    },
    slotLabels(rows) {
      return rows.map((r) => r.label);
    },
    pad(n) {
      return (n < 10 ? "0" : "") + n;
    },
    metricLabel(m) {
      return this.$t("creationSummaryReport.metric_" + m);
    },
    metricUnit(m) {
      if (m === "b") return this.$t("creationSummaryReport.bill_unit");
      if (m === "q") return this.$t("creationSummaryReport.box_unit");
      return this.$t("creationSummaryReport.kip_unit");
    },
    provinceName(id) {
      if (!id) return this.$t("creationSummaryReport.no_province");
      const p = this.report && this.report.provinces.find((x) => x.province_id === id);
      return p ? p.name : "#" + id;
    },
    branchTypeLabel(type) {
      if (!type) return "-";
      const key = "creationSummaryReport.branch_type_" + type;
      const label = this.$t(key);
      return label === key ? type : label;
    },
    disabledAfterToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    formatDateTime(value) {
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatMoney(value) {
      return this.formatNumber(value || 0) + " " + this.$t("creationSummaryReport.kip_unit");
    },
    quickWindow(daysAgo) {
      const day = moment(new Date()).subtract(daysAgo, "days").format("YYYY-MM-DD");
      this.from = day;
      this.to = day;
      this.fetchData();
    },
    selectDay(day) {
      // "ຂໍດຶງຍອດມື້ວານ": the whole page becomes that single day, so the time-slot
      // and branch splits below are that day's.
      this.from = day;
      this.to = day;
      this.fetchData();
    },
    onDayBarClick({index}) {
      const row = this.dailyRows[index];
      if (row && !row.missing && this.dailyRows.length > 1) this.selectDay(row.key);
    },
    onProvinceBarClick({index}) {
      const row = this.provinceRows[index];
      if (row) this.selectProvince(row.key);
    },
    selectProvince(provinceId) {
      this.selectedProvinceId = this.selectedProvinceId === provinceId ? null : provinceId;
      if (this.selectedProvinceId === null) return;
      this.$nextTick(() => {
        if (this.$refs.branchBox) this.$refs.branchBox.scrollIntoView({behavior: "smooth", block: "start"});
      });
    },
    selectBranch(branchId) {
      this.selectedBranchId = branchId;
      this.fetchBranch();
      this.$nextTick(() => {
        if (this.$refs.branchDrill) this.$refs.branchDrill.scrollIntoView({behavior: "smooth", block: "start"});
      });
    },
    closeBranch() {
      this.branchSeq++; // an in-flight drill response must not reopen the box
      this.selectedBranchId = null;
      this.branchReport = null;
      this.branchLoading = false;
    },
    fetchData() {
      const seq = ++this.requestSeq;
      this.loading = true;
      fetchCreationSummaryReport({from: this.from, to: this.to})
        .then((data) => {
          if (seq !== this.requestSeq) return;
          this.report = data;
          this.validateSelections();
          this.loading = false;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          console.log(error);
          this.$vs.notification({color: "danger", title: this.$t("creationSummaryReport.error_loading")});
          this.loading = false;
        });
    },
    /**
     * After a refetch or a tab change: drop a branch type / province / branch
     * that the CURRENT tab's folded data no longer contains (computed props
     * already reflect the new report when read here).
     */
    validateSelections() {
      if (this.branchType && !this.branchTypes.includes(this.branchType)) {
        this.branchType = "";
      }
      const visible = this.filteredBranches;
      if (this.selectedProvinceId !== null && !visible.some((b) => (b.provinceId || 0) === this.selectedProvinceId)) {
        this.selectedProvinceId = null;
      }
      if (this.selectedBranchId !== null) {
        if (visible.some((b) => b.key === this.selectedBranchId)) {
          this.fetchBranch();
        } else {
          this.closeBranch();
        }
      }
    },
    fetchBranch() {
      if (this.selectedBranchId === null || !this.report) return;
      const seq = ++this.branchSeq;
      // Never show branch A's (or an older window's) numbers under branch B's title.
      this.branchReport = null;
      this.branchLoading = true;
      // The LOADED window, not the pickers: the drill must match the tables above it.
      fetchCreationSummaryReport({from: this.report.from, to: this.report.to, branch_id: this.selectedBranchId})
        .then((data) => {
          if (seq !== this.branchSeq) return;
          this.branchReport = data;
          this.branchLoading = false;
        })
        .catch((error) => {
          if (seq !== this.branchSeq) return;
          console.log(error);
          this.$vs.notification({color: "danger", title: this.$t("creationSummaryReport.error_loading")});
          this.branchLoading = false;
        });
    },
    rowToSheet(row, firstHeader, firstValue) {
      const t = (key) => this.$t("creationSummaryReport." + key);
      const out = {[firstHeader]: firstValue};
      if (row.missing !== undefined) {
        out[t("status")] = row.missing ? t("not_built") : "";
      }
      TYPE_KEYS.concat(["total"]).forEach((type) => {
        const label = type === "total" ? t("total") : t(type === "other" ? "other_type" : type);
        out[label + " - " + t("bills")] = row.missing ? "" : row[type].b;
        out[label + " - " + t("pieces")] = row.missing ? "" : row[type].q;
        out[label + " - " + t("freight")] = row.missing ? "" : row[type].f;
        out[label + " - " + t("price")] = row.missing ? "" : row[type].v;
      });
      return out;
    },
    exportExcel() {
      if (!this.report) return;
      const t = (key) => this.$t("creationSummaryReport." + key);
      const wb = XLSX.utils.book_new();

      const info = [
        {[t("info_key")]: t("from_date"), [t("info_value")]: this.formatDate(this.report.from)},
        {[t("info_key")]: t("to_date"), [t("info_value")]: this.formatDate(this.report.to)},
        {[t("info_key")]: t("tab"), [t("info_value")]: t("tab_" + this.tab)},
        {[t("info_key")]: t("branch_type"), [t("info_value")]: this.branchType ? this.branchTypeLabel(this.branchType) : t("branch_type_all")},
        {[t("info_key")]: t("as_of"), [t("info_value")]: this.report.as_of ? this.formatDateTime(this.report.as_of) : "-"},
        {[t("info_key")]: t("total_bills"), [t("info_value")]: this.totals.b},
        {[t("info_key")]: t("total_freight"), [t("info_value")]: this.totals.f},
        {[t("info_key")]: t("total_price"), [t("info_value")]: this.totals.v},
        {[t("info_key")]: t("total_pieces"), [t("info_value")]: this.totals.q},
      ];
      if (this.report.missing_days && this.report.missing_days.length) {
        info.push({[t("info_key")]: t("warning"), [t("info_value")]: t("missing_days") + ": " + this.report.missing_days.map((d) => this.formatDate(d)).join(", ")});
      }
      info.push({[t("info_key")]: t("definition_note"), [t("info_value")]: ""});
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(info), "Info");

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(this.dailyRows.map((r) => this.rowToSheet(r, t("date"), this.formatDate(r.key)))),
        "Daily"
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(this.slotRows.map((r) => this.rowToSheet(r, t("slot"), r.label))),
        "Hours"
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(this.provinceRows.map((r) => {
          const out = this.rowToSheet(r, t("province"), r.label);
          out[t("branch_count")] = r.branchCount;
          return out;
        })),
        "Provinces"
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(this.filteredBranches
          .filter((r) => !this.branchType || r.type === this.branchType)
          .sort((a, b) => b.total[this.metric] - a.total[this.metric])
          .map((r) => {
            const out = this.rowToSheet(r, t("branch"), r.label);
            out[t("branch_type")] = this.branchTypeLabel(r.type);
            out[t("province")] = this.provinceName(r.provinceId);
            return out;
          })),
        "Branches"
      );
      if (this.branchReport && this.selectedBranchId !== null) {
        const name = this.selectedBranchName.replace(/[\\/?*[\]:]/g, " ").replace(/^'+|'+$/g, "").trim().substring(0, 20);
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(this.branchDailyRows.map((r) => this.rowToSheet(r, t("date"), this.formatDate(r.key)))),
          ("Daily-" + name).substring(0, 31)
        );
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(this.branchSlotRows.map((r) => this.rowToSheet(r, t("slot"), r.label))),
          ("Hours-" + name).substring(0, 31)
        );
      }

      XLSX.writeFile(
        wb,
        "creation-summary-report " + t("tab_" + this.tab) + " " + this.formatDate(this.report.from).replace(/\//g, "-") + " ຫາ " + this.formatDate(this.report.to).replace(/\//g, "-") + ".xlsx"
      );
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.chart-scroll {
  overflow-x: auto;
  margin-bottom: 1rem;
}

.chart-wrapper {
  position: relative;
  // vue-chartjs renders <div><canvas width=400 height=400> with NO height on its
  // div, so Chart.js (responsive, maintainAspectRatio: false) keeps the 400px
  // canvas default instead of this wrapper's height. A shorter wrapper then
  // hides the x-axis labels behind a vertical scrollbar. Hand the chart root the
  // wrapper's height so the plot always fits exactly.
  > div {
    height: 100%;
  }
}

.report-tabs {
  margin-bottom: 1rem;

  li {
    cursor: pointer;
  }
}

.is-clickable {
  cursor: pointer;
}

.is-selected-row {
  background-color: #fff8e6 !important;
}

.is-missing-row td {
  background-color: #fff5f5;
}

.is-empty-row td {
  color: #a0a0a0;
}

.grand-total-row {
  background-color: #f5f5f5;
}

.branch-table {
  max-height: 520px;
  overflow-y: auto;
}

.drill-header {
  margin-bottom: 0.5rem;
}

.section-title {
  margin-top: 1rem;
  margin-bottom: 0.25rem;
}

.totals-box {
  padding: 0.75rem 1.25rem;
}

.box {
  @media (max-width: 768px) {
    padding: 1rem;
  }
}

.buttons {
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    .button {
      margin-bottom: 0.5rem;
      width: 100%;
    }
  }

  @media (min-width: 769px) {
    .button {
      margin-right: 0.5rem;
    }
  }
}

::v-deep .mx-datepicker {
  width: 100%;

  .mx-input {
    width: 100%;
  }
}
</style>
